import { useCallback, useEffect, useRef, useState } from 'react';
import { notification } from 'tea-component';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import useLanguage from '@op/hooks/useLanguage';
import {
  getReportPermissionInfo,
  type IAuthorizedUser,
  type IReportPermissionInfo,
  type IReportPermissionTarget,
  type TPermissionLevel,
} from '@op/services/report';

export function useReportPermissionInfo(
  target: IReportPermissionTarget | null,
  visible: boolean,
) {
  const { formatMessage } = useLanguage();
  const [permissionLevel, setPermissionLevel] =
    useState<TPermissionLevel>('private');
  const [selectedUsers, setSelectedUsers] = useState<IAuthorizedUser[]>([]);

  const clearPermissionForm = useCallback(() => {
    setPermissionLevel('private');
    setSelectedUsers([]);
  }, []);

  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    clear: clearPermissionForm,
  });

  const targetRef = useRef<IReportPermissionTarget | null>(null);

  useEffect(() => {
    if (!visible || !target) {
      clearPermissionForm();
      targetRef.current = null;
      return;
    }

    const nextTarget = {
      object_type: target.object_type,
      object_id: target.object_id,
    };
    targetRef.current = nextTarget;

    wrapperCancelRequest(getReportPermissionInfo, nextTarget)
      .then((data) => {
        const result = data as IReportPermissionInfo | null;
        if (
          !result ||
          targetRef.current?.object_id !== nextTarget.object_id
        ) {
          return;
        }
        setPermissionLevel(result.permission_level || 'private');
        setSelectedUsers(result.authorized_users_info || []);
      })
      .catch((error: { message?: string; }) => {
        notification.error({
          description:
            error?.message || formatMessage({ id: 'Request failed' }),
        });
      });
  }, [
    clearPermissionForm,
    formatMessage,
    target?.object_id,
    target?.object_type,
    visible,
    wrapperCancelRequest,
  ]);

  return {
    loading,
    permissionLevel,
    setPermissionLevel,
    selectedUsers,
    setSelectedUsers,
  };
}
